import createModule from "../createModule"
import view from "./view"
import { Creators } from "./model"
import { Creators as PortfolioRootCreators } from "modules/portfolio-root/model"
import { Creators as DialogCreators } from "modules/dialog/model"
import { getViewType, getPeriod, getPortfolioId, getSelectedModelId, getIsExpanded, getIsActive, getCurrency, getPortfolioFxHedgeEnabled } from "modules/portfolio-root/selectors/portfolios"
import { getBaseStartDate } from "modules/portfolio-root/selectors/base"
import { getEarliestDate, getLatestDate } from "modules/portfolio-root/selectors/dates"
import { getStartDate, getEndDate, getSelectedDate, getDriftDates, getAiSummaryStartDate } from "selectors/dates"
import { Creators as notificationsCreators } from "modules/notifications/model"
import { getOptions, getSelectedOption, getFilteredData, getCategoryItemsSubset, getDrift, getPassthroughResourcesStatus, getIsContentSetAndUnavailable } from "./selectors/data"
import { getBaseSegmentSignature, getBasePositionSetWithSegment } from "selectors/base"
import { getSelectedViews } from "selectors/factors"
import { getDisplayType, getDisplayTypeName, getAvailableSelectViewFactors, getDisplayAddView, getDisplayAggregationToggle } from "./selectors/items"
import { getFileName } from "./selectors/file"
import { getDatesAdjusted } from "./selectors/dates"
import { getExposureUnit, getFirstHistoricalExposureDate, getIsSensitivityContentSet } from "./selectors/exposure"
import { getEffectiveSegment } from "modules/analyze-header/selectors/effective-segment"
import { getFiltersVisible } from "modules/analyze-header/selectors"
import { getStyleAggregation } from "selectors/styleAggregation"
import withUrlParams from "hocs/withUrlParams"
import { MODEL_METADATA_QUERY } from "resources/models"

export default withUrlParams(createModule({
  view,
  externalProps: (state, props) => ({
    portfolioShortId: state.global.selected.portfolio,
    portfolioId: getPortfolioId(state, props),
    account: state.global.selected.account,
    accountId: state.global.selected.accountId,
    modelId: getSelectedModelId(state, props),
    driftDates: getDriftDates(state, props),
    startDate: getStartDate(state, props),
    aiSummaryStartDate: getAiSummaryStartDate(state, props),
    endDate: getEndDate(state, props),
    selectedDate: getSelectedDate(state, props),
    viewType: getViewType(state, props),
    base: getBasePositionSetWithSegment(state, props),
    baseSegmentSignature: getBaseSegmentSignature(state, props),
    baseStartDate: getBaseStartDate(state, props),
    segment: getEffectiveSegment(state, props),
    expand: getIsExpanded(state, props),
    isActive: getIsActive(state, props),
    isStyleFactorsAdditive: false,
    isSensitivityContentSet: getIsSensitivityContentSet(state, props),
    fxHedgeEnabled: getPortfolioFxHedgeEnabled(state, props),
  }),
  required: ["account", "portfolioShortId", "modelId"],
  data: [{
    resource: "portfolio",
    as: null,
    url: ({ account, portfolioShortId }) => `/api/${account}/p/${portfolioShortId}`,
  }, {
    resource: "exposure",
    as: null,
    passthrough: true,
    url: ({ portfolioId, modelId, driftDates, isActive, startDate, base, baseSegmentSignature, baseStartDate, expand, params: { contentSetId }, segment, fxHedgeEnabled }) => {
      let from = startDate < driftDates.startDate ? startDate : driftDates.startDate
      const to = driftDates.endDate

      if (isActive && from < baseStartDate) {
        from = baseStartDate
      }

      return {
        id: `exposure-${modelId}${portfolioId}${from}${to}${isActive}${baseSegmentSignature}${contentSetId}${JSON.stringify(segment)}${expand}${fxHedgeEnabled}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $from: Date!, $to: Date!, $base: [PositionSetInput], $contentSetId: String) {
          model (id: $modelId) {
            simulation (positionSet: $positionSet, from: $from, to: $to, base: $base, interval: AUTO) {
              exposure (contentSetId: $contentSetId) {
                date
                factors {
                  id
                  name
                  category
                  exposure: net
                  currencyExposure
                  long
                  short
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          positionSet: {
            type: "PORTFOLIO",
            id: portfolioId,
            segment,
            expand,
          },
          from,
          to,
          contentSetId,
          base: isActive ? [base] : undefined,
        },
      }
    },
  },
  MODEL_METADATA_QUERY,
  ],
  mapStateToProps: (state, props) => ({
    earliestDate: getEarliestDate(state, props),
    latestDate: getLatestDate(state, props),
    data: getFilteredData(state, props),
    items: getCategoryItemsSubset(state, props),
    options: getOptions(state, props),
    period: getPeriod(state, props),
    drift: getDrift(state, props),
    firstDate: getFirstHistoricalExposureDate(state, props),
    fileName: getFileName(state, props),
    ...state.modules.analyzeHistoricalExposure,
    displayType: getDisplayType(state, props),
    displayTypeName: getDisplayTypeName(state, props),
    selectedOption: getSelectedOption(state, props),
    filtersVisible: getFiltersVisible(state, props),
    datesAdjusted: getDatesAdjusted(state, props),
    selectedView: getSelectedViews(state, props),
    displayAddView: getDisplayAddView(state, props),
    availableSelectViewFactors: getAvailableSelectViewFactors(state, props),
    ...getStyleAggregation(state, props),
    displayAggregationToggle: getDisplayAggregationToggle(state, props),
    currency: getCurrency(state, props),
    exposureUnit: getExposureUnit(state, props),
    passthroughResourcesStatus: getPassthroughResourcesStatus(state, props),
    isContentSetAndUnavailable: getIsContentSetAndUnavailable(state, props),
  }),
  actions: {
    ...Creators,
    setDate: PortfolioRootCreators.setDate,
    resetDate: PortfolioRootCreators.resetDate,
    setPeriod: PortfolioRootCreators.setPeriod,
    addSelectedView: PortfolioRootCreators.addSelectedView,
    removeSelectedView: PortfolioRootCreators.removeSelectedView,
    addNotification: notificationsCreators.add,
    openDialog: DialogCreators.open,
    setAggregationFn: PortfolioRootCreators.setAggregationFn,
  },
}))
